import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Episode, MediaType } from '../types';
import { analytics } from '../services/analyticsService';

interface MediaContextType {
  currentEpisode: Episode | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  isMinimized: boolean;
  playbackRate: number;
  volume: number;
  play: (episode: Episode) => void;
  pause: () => void;
  resume: () => void;
  seek: (time: number) => void;
  changeVolume: (val: number) => void;
  changePlaybackRate: (rate: number) => void;
  toggleMinimize: () => void;
  closePlayer: () => void;
  mediaRef: React.MutableRefObject<HTMLMediaElement | null>;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

const POSITIONS_KEY = 'nidastudio_positions';
const VOLUME_KEY = 'nidastudio_volume';

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem(VOLUME_KEY);
    return saved ? parseFloat(saved) : 1;
  });

  const mediaRef = useRef<HTMLMediaElement | null>(null);

  // Resume tracking: Map of episodeId -> currentTime
  const getPositions = () => {
    const saved = localStorage.getItem(POSITIONS_KEY);
    return saved ? JSON.parse(saved) : {};
  };

  const savePosition = (id: string, time: number) => {
    const positions = getPositions();
    positions[id] = time;
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(positions));
  };

  const getPosition = (id: string) => {
    const positions = getPositions();
    return positions[id] || 0;
  };

  // Sync Volume
  useEffect(() => {
    localStorage.setItem(VOLUME_KEY, volume.toString());
    if (mediaRef.current) {
        mediaRef.current.volume = volume;
    }
  }, [volume]);

  // Periodic Save & Analytics Batching
  useEffect(() => {
    const interval = setInterval(() => {
      if (mediaRef.current && isPlaying && currentEpisode) {
        // Save Time for this specific episode
        savePosition(currentEpisode.id, mediaRef.current.currentTime);
        
        // Log Analytics
        analytics.logAudioListen(currentEpisode.id, 5);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, currentEpisode]);

  const play = (episode: Episode) => {
    if (currentEpisode?.id === episode.id) {
      resume();
      return;
    }
    
    // Check for previous position
    const lastPos = getPosition(episode.id);
    
    setCurrentEpisode(episode);
    setIsMinimized(false);
    setProgress(lastPos);
    setIsPlaying(true); 
    
    // Analytics: Log play start
    analytics.logAudioPlay(episode.id);
  };

  const pause = () => {
    if (mediaRef.current) {
      mediaRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resume = () => {
    if (mediaRef.current) {
      mediaRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  const seek = (time: number) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = time;
      setProgress(time);
    }
  };

  const changeVolume = (val: number) => {
    setVolume(val);
  };

  const changePlaybackRate = (rate: number) => {
    setPlaybackRate(rate);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = rate;
    }
  };

  const toggleMinimize = () => setIsMinimized(prev => !prev);

  const closePlayer = () => {
    if (mediaRef.current && currentEpisode) {
      savePosition(currentEpisode.id, mediaRef.current.currentTime);
      mediaRef.current.pause();
    }
    setCurrentEpisode(null);
    setIsPlaying(false);
  };

  // Sync state with ref events
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;

    const updateTime = () => setProgress(el.currentTime);
    const updateDuration = () => setDuration(el.duration);
    const handleEnded = () => {
        setIsPlaying(false);
        if (currentEpisode) savePosition(currentEpisode.id, 0); // Reset on finish
    };
    
    // When metadata loads, resume to last known position
    const handleLoadedMetadata = () => {
        updateDuration();
        if (currentEpisode) {
            const lastPos = getPosition(currentEpisode.id);
            if (lastPos > 0 && lastPos < el.duration) {
                el.currentTime = lastPos;
            }
        }
    };

    el.addEventListener('timeupdate', updateTime);
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    el.addEventListener('ended', handleEnded);
    
    // Force initial volume
    el.volume = volume;

    return () => {
      el.removeEventListener('timeupdate', updateTime);
      el.removeEventListener('loadedmetadata', handleLoadedMetadata);
      el.removeEventListener('ended', handleEnded);
    };
  }, [currentEpisode]);

  // Media Session API integration
  useEffect(() => {
    if (!currentEpisode || !('mediaSession' in navigator)) return;

    const showTitle = (currentEpisode as any)?.show?.title;
    const artwork = currentEpisode.thumbnail
      ? [{ src: currentEpisode.thumbnail, sizes: '512x512', type: 'image/jpeg' }]
      : undefined;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentEpisode.title,
      artist: showTitle || 'Nida Studios',
      album: showTitle || 'Nida Studios',
      artwork
    });

    navigator.mediaSession.setActionHandler('play', resume);
    navigator.mediaSession.setActionHandler('pause', pause);
    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      const offset = details.seekOffset || 15;
      seek(Math.max(0, progress - offset));
    });
    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      const offset = details.seekOffset || 30;
      seek(Math.min(duration || Infinity, progress + offset));
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime !== undefined) {
        seek(details.seekTime);
      }
    });
    navigator.mediaSession.setActionHandler('stop', () => {
      pause();
      closePlayer();
    });
  }, [currentEpisode, duration, pause, progress, resume, seek]);

  return (
    <MediaContext.Provider value={{
      currentEpisode,
      isPlaying,
      progress,
      duration,
      isMinimized,
      playbackRate,
      volume,
      play,
      pause,
      resume,
      seek,
      changeVolume,
      changePlaybackRate,
      toggleMinimize,
      closePlayer,
      mediaRef
    }}>
      {children}
    </MediaContext.Provider>
  );
};

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (!context) throw new Error("useMedia must be used within MediaProvider");
  return context;
};
